// VaultsTab — Settings surface for per-vault management.
//
// Reads `useMasterSession.appConfig.vaults` and renders one editable
// row per vault. The display name is **canonical** in appConfig
// (paired with `useVaultDisplayName`); a secondary `vault.setDisplayName`
// is fired only when the renamed vault is currently open. Toggle
// sync flows require the vault to be unlocked because they call
// `changePassphrase` on the open handle. Remove is a typed-confirm
// step that filters the entry from `appConfig.vaults`; the master
// (OPFS-root) vault is never removable from this surface — wiping
// the app drops it via the master unlock screen instead.
//
// Per-vault retry of pending master rotations uses the `vaultIds`
// filter on `retryPendingRotations` so a single failed vault can be
// retried without reopening any others.

import { useMemo, useState } from "react";
import { Star, Pencil, RefreshCcw, Trash2 } from "lucide-react";
import { zxcvbn } from "@zxcvbn-ts/core";
import { cn } from "../../lib/utils";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { useMasterSession } from "../../stores/masterSession";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import {
  retryPendingRotations,
  type RotationProgress,
} from "../../lib/masterRotation";
import type { AppConfig, VaultEntry } from "../../worker/api";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";
import { MasterRotationModal } from "../lifecycle/MasterRotationModal";

function isMasterEntry(v: VaultEntry): boolean {
  return v.adapter.type === "opfs" && v.adapter.subdir === "";
}

function adapterSubtitle(v: VaultEntry): string {
  if (v.adapter.type === "opfs") {
    return v.adapter.subdir === "" ? "OPFS (master)" : `OPFS / ${v.adapter.subdir}`;
  }
  const a = v.adapter;
  return a.prefix ? `${a.bucket}/${a.prefix}` : a.bucket;
}

function formatLastOpened(seconds: number | null | undefined): string {
  if (!seconds) return "never";
  const ageSec = Math.floor(Date.now() / 1000) - seconds;
  if (ageSec < 60) return "just now";
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

export function VaultsTab() {
  const worker = useVault();
  const session = useSession((s) => s.session);
  const appConfig = useMasterSession((s) => s.master?.appConfig);
  const masterHandle = useMasterSession((s) => s.master?.masterHandle);
  const setAppConfig = useMasterSession((s) => s.setAppConfig);

  const [removeTarget, setRemoveTarget] = useState<VaultEntry | null>(null);
  const [toggleTarget, setToggleTarget] = useState<VaultEntry | null>(null);
  const [retryTarget, setRetryTarget] = useState<VaultEntry | null>(null);
  const [rotationProgress, setRotationProgress] =
    useState<RotationProgress | null>(null);
  // Inline error surfaced when an async per-row operation rejects.
  // Keeps `void asyncFn(...)` from becoming an unhandled rejection
  // (which crashes dev tools and, in some browsers, the whole tab).
  const [opError, setOpError] = useState<string | null>(null);

  if (!appConfig || masterHandle === undefined) {
    return (
      <div className="max-w-xl">
        <h2 className="text-xl font-medium">Vaults</h2>
        <p className="mt-3 text-sm text-text-soft">
          No master session — this should not be reachable.
        </p>
      </div>
    );
  }

  const persist = async (updated: AppConfig): Promise<void> => {
    await worker.saveAppConfig(masterHandle, updated);
    setAppConfig(updated);
  };

  /**
   * Run a fire-and-forget async handler with a sticky catch so the
   * promise never escapes as `unhandledrejection`. The user sees the
   * failure inline at the top of the tab.
   */
  const fireAndCatch = (label: string, fn: () => Promise<void>): void => {
    fn().catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      setOpError(`${label}: ${msg}`);
    });
  };

  const renameVault = async (
    target: VaultEntry,
    newName: string,
  ): Promise<void> => {
    const trimmed = newName.trim();
    if (trimmed.length === 0) return;
    const updated: AppConfig = {
      ...appConfig,
      vaults: appConfig.vaults.map((v) =>
        v.id === target.id ? { ...v, display_name: trimmed } : v,
      ),
    };
    await persist(updated);
    if (session?.vaultId === target.id) {
      try {
        await worker.setDisplayName(session.handle, trimmed);
      } catch {
        // Best-effort secondary write — appConfig is canonical.
      }
    }
  };

  const removeVault = async (target: VaultEntry): Promise<void> => {
    if (isMasterEntry(target)) return;
    const updated: AppConfig = {
      ...appConfig,
      vaults: appConfig.vaults.filter((v) => v.id !== target.id),
    };
    await persist(updated);
    setRemoveTarget(null);
  };

  const reassignPrimary = async (target: VaultEntry): Promise<void> => {
    if (appConfig.primary_vault_id === target.id) return;
    const updated: AppConfig = {
      ...appConfig,
      primary_vault_id: target.id,
    };
    await persist(updated);
  };

  const applySyncToggle = async (
    target: VaultEntry,
    args:
      | { kind: "to-unsynced"; masterPass: string; newVaultPass: string }
      | { kind: "to-synced"; currentVaultPass: string; masterPass: string },
  ): Promise<void> => {
    if (session?.vaultId !== target.id) {
      throw new Error("vault must be open");
    }
    if (args.kind === "to-unsynced") {
      const masterBytes = toPassphraseBytes(args.masterPass);
      const newBytes = toPassphraseBytes(args.newVaultPass);
      try {
        await worker.changePassphrase(session.handle, masterBytes, newBytes);
      } finally {
        zeroize(masterBytes);
        zeroize(newBytes);
      }
    } else {
      const oldBytes = toPassphraseBytes(args.currentVaultPass);
      const masterBytes = toPassphraseBytes(args.masterPass);
      try {
        await worker.changePassphrase(session.handle, oldBytes, masterBytes);
      } finally {
        zeroize(oldBytes);
        zeroize(masterBytes);
      }
    }
    const nextSync = args.kind === "to-unsynced" ? "unsynced" : "synced";
    const updated: AppConfig = {
      ...appConfig,
      vaults: appConfig.vaults.map((v) =>
        v.id === target.id ? { ...v, sync_state: nextSync } : v,
      ),
    };
    await persist(updated);
    setToggleTarget(null);
  };

  const startRetry = (
    target: VaultEntry,
    oldMaster: string,
    currentMaster: string,
  ): void => {
    setRetryTarget(null);
    // `retryPendingRotations` emits an `aborted` phase via
    // `onProgress` before rethrowing on saveAppConfig failure (see
    // `masterRotation.ts`); the modal renders that surface and
    // exposes the Close button. We swallow the rejection here so it
    // doesn't bubble up as an unhandled rejection. If a future
    // change makes the orchestrator throw without first emitting an
    // aborted phase, fall back to the top-level `opError` banner so
    // the user is never left looking at a stuck spinner with no
    // explanation.
    retryPendingRotations(worker, oldMaster, currentMaster, {
      vaultIds: [target.id],
      onProgress: setRotationProgress,
    }).catch((err: unknown) => {
      // If onProgress already set rotationProgress to an aborted
      // phase, the modal is doing the talking — stay silent.
      // Otherwise (defensive), surface the failure inline.
      setRotationProgress((prev) => {
        if (prev !== null && prev.phase.kind === "aborted") {
          return prev;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setOpError(`Retry failed: ${msg}`);
        return prev;
      });
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h2 className="text-xl font-medium">Vaults</h2>
      <p className="max-w-[60ch] text-xs text-text-soft">
        Each row is one vault registered on this device. Renaming, sync
        state, and primary assignment are stored in the encrypted
        app-config.
      </p>

      {opError !== null && (
        <div
          role="alert"
          className="rounded-md border border-danger/40 bg-danger/10 p-3 text-[13px] text-danger"
        >
          <div className="flex items-start justify-between gap-3">
            <span className="font-mono">{opError}</span>
            <button
              type="button"
              onClick={() => setOpError(null)}
              aria-label="Dismiss error"
              className="rounded text-text-soft hover:text-text"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <ul className="divide-y divide-border border-y border-border">
        {appConfig.vaults.map((entry) => (
          <VaultRow
            key={entry.id}
            entry={entry}
            isPrimary={appConfig.primary_vault_id === entry.id}
            isOpen={session?.vaultId === entry.id}
            onRename={(newName) =>
              fireAndCatch("Rename failed", () => renameVault(entry, newName))
            }
            onTogglePrimary={() =>
              fireAndCatch("Set primary failed", () => reassignPrimary(entry))
            }
            onAskRemove={() => setRemoveTarget(entry)}
            onAskToggleSync={() => setToggleTarget(entry)}
            onAskRetry={() => setRetryTarget(entry)}
          />
        ))}
      </ul>

      {removeTarget !== null && (
        <RemoveVaultDialog
          target={removeTarget}
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() =>
            fireAndCatch("Remove failed", () => removeVault(removeTarget))
          }
        />
      )}

      {toggleTarget !== null && (
        <ToggleSyncDialog
          target={toggleTarget}
          isOpen={session?.vaultId === toggleTarget.id}
          onCancel={() => setToggleTarget(null)}
          onApply={(args) =>
            fireAndCatch("Sync toggle failed", () =>
              applySyncToggle(toggleTarget, args),
            )
          }
        />
      )}

      {retryTarget !== null && (
        <RetryDialog
          target={retryTarget}
          onCancel={() => setRetryTarget(null)}
          onSubmit={(oldM, curM) => startRetry(retryTarget, oldM, curM)}
        />
      )}

      {rotationProgress !== null && (
        <MasterRotationModal
          progress={rotationProgress}
          onClose={() => setRotationProgress(null)}
        />
      )}
    </div>
  );
}

// ————— Row ——————————————————————————————————————————————————

interface VaultRowProps {
  readonly entry: VaultEntry;
  readonly isPrimary: boolean;
  readonly isOpen: boolean;
  readonly onRename: (newName: string) => void;
  readonly onTogglePrimary: () => void;
  readonly onAskRemove: () => void;
  readonly onAskToggleSync: () => void;
  readonly onAskRetry: () => void;
}

function VaultRow(props: VaultRowProps) {
  const {
    entry,
    isPrimary,
    onRename,
    onTogglePrimary,
    onAskRemove,
    onAskToggleSync,
    onAskRetry,
  } = props;
  const isMaster = isMasterEntry(entry);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.display_name);

  const commit = (): void => {
    if (draft.trim().length === 0) return;
    if (draft.trim() !== entry.display_name) {
      onRename(draft);
    }
    setEditing(false);
  };

  return (
    <li className="flex flex-wrap items-start gap-3 px-1 py-3 text-sm">
      <button
        type="button"
        onClick={onTogglePrimary}
        disabled={isPrimary}
        aria-label={
          isPrimary
            ? `${entry.display_name} is primary`
            : `Set ${entry.display_name} as primary`
        }
        className={cn(
          "mt-0.5 rounded p-1",
          isPrimary ? "text-accent" : "text-text-dim hover:text-text",
        )}
      >
        <Star className="h-4 w-4" fill={isPrimary ? "currentColor" : "none"} />
      </button>

      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            autoFocus
            aria-label={`New name for ${entry.display_name}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(entry.display_name);
                setEditing(false);
              }
            }}
            onBlur={() => {
              if (draft.trim() === entry.display_name || draft.trim().length === 0) {
                setEditing(false);
              } else {
                commit();
              }
            }}
            className="w-full rounded-md border border-border bg-white/5 px-2 py-1 text-sm"
          />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{entry.display_name}</span>
            <SyncPill state={entry.sync_state} />
            {entry.pending_rotation !== null && (
              <button
                type="button"
                onClick={onAskRetry}
                aria-label={`Retry ${entry.display_name}`}
                className="rounded-full border border-warn/40 bg-warn/10 px-2 py-0.5 text-[11px] text-warn hover:bg-warn/20"
              >
                Pending rotation · Retry
              </button>
            )}
          </div>
        )}
        <div className="mt-1 text-[11px] text-text-dim">
          {adapterSubtitle(entry)} · last opened {formatLastOpened(entry.last_opened_at)}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setEditing((e) => !e)}
          aria-label={`Edit name ${entry.display_name}`}
          className="rounded p-1.5 text-text-dim hover:bg-accent-bg hover:text-text"
        >
          <Pencil className="h-4 w-4" />
        </button>
        {!isMaster && (
          <button
            type="button"
            onClick={onAskToggleSync}
            aria-label={`Toggle sync ${entry.display_name}`}
            className="rounded p-1.5 text-text-dim hover:bg-accent-bg hover:text-text"
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onAskRemove}
          disabled={isMaster}
          aria-label={`Remove ${entry.display_name}`}
          title={
            isMaster
              ? "The master vault cannot be removed. Use 'Wipe app' from the master unlock screen."
              : undefined
          }
          className="rounded p-1.5 text-text-dim hover:bg-danger/20 hover:text-danger disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-text-dim"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

function SyncPill({ state }: { state: "synced" | "unsynced" }) {
  if (state === "synced") {
    return (
      <span className="rounded-full bg-ok/15 px-2 py-0.5 text-[11px] text-ok">
        Synced
      </span>
    );
  }
  return (
    <span className="rounded-full bg-warn/15 px-2 py-0.5 text-[11px] text-warn">
      Unsynced
    </span>
  );
}

// ————— Remove dialog ————————————————————————————————————————

function RemoveVaultDialog({
  target,
  onCancel,
  onConfirm,
}: {
  readonly target: VaultEntry;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const armed = typed.trim() === target.display_name;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Remove ${target.display_name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-medium tracking-tight">
          Remove vault from this device
        </h2>
        <p className="mb-3 text-[13px] leading-relaxed text-text-soft">
          The vault's storage data will <strong>not</strong> be deleted. You can
          re-add it later via "Add vault". Type{" "}
          <code className="rounded bg-white/5 px-1 py-0.5 text-[12px] text-text">
            {target.display_name}
          </code>{" "}
          to confirm.
        </p>
        <input
          autoFocus
          aria-label="Type the vault name to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full rounded-md border border-border bg-white/5 px-3 py-2 text-sm"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!armed}
            className="rounded-md bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            Remove vault
          </button>
        </div>
      </div>
    </div>
  );
}

// ————— Toggle sync dialog ——————————————————————————————————

function ToggleSyncDialog({
  target,
  isOpen,
  onCancel,
  onApply,
}: {
  readonly target: VaultEntry;
  readonly isOpen: boolean;
  readonly onCancel: () => void;
  readonly onApply: (
    args:
      | { kind: "to-unsynced"; masterPass: string; newVaultPass: string }
      | { kind: "to-synced"; currentVaultPass: string; masterPass: string },
  ) => void;
}) {
  const [masterPass, setMasterPass] = useState("");
  const [newVaultPass, setNewVaultPass] = useState("");
  const [currentVaultPass, setCurrentVaultPass] = useState("");
  const goingToUnsynced = target.sync_state === "synced";
  const newScore = useMemo(
    () => (newVaultPass.length > 0 ? zxcvbn(newVaultPass).score : 0),
    [newVaultPass],
  );
  const newStrong = newScore >= 3;
  const canSubmit = goingToUnsynced
    ? masterPass.length > 0 && newStrong
    : currentVaultPass.length > 0 && masterPass.length > 0;

  if (!isOpen) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Toggle sync ${target.display_name}`}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      >
        <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
          <h2 className="mb-2 text-lg font-medium tracking-tight">
            Vault must be unlocked
          </h2>
          <p className="mb-4 text-[13px] leading-relaxed text-text">
            Please unlock this vault first to change its sync state.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const submit = (): void => {
    if (!canSubmit) return;
    if (goingToUnsynced) {
      onApply({ kind: "to-unsynced", masterPass, newVaultPass });
    } else {
      onApply({ kind: "to-synced", currentVaultPass, masterPass });
    }
    setMasterPass("");
    setNewVaultPass("");
    setCurrentVaultPass("");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Toggle sync ${target.display_name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-medium tracking-tight">
          {goingToUnsynced
            ? "Switch to unsynced (separate passphrase)"
            : "Switch to synced (master password)"}
        </h2>
        <p className="mb-4 text-[13px] leading-relaxed text-text-soft">
          {goingToUnsynced
            ? "This vault will use a separate passphrase that you type each time. The master password will no longer unlock it."
            : "This vault will use the master password going forward."}
        </p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {!goingToUnsynced && (
            <div>
              <label
                htmlFor="toggle-current-vault-pass"
                className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
              >
                Current vault passphrase
              </label>
              <PassphraseInput
                id="toggle-current-vault-pass"
                autoCompleteMode="current-password"
                aria-label="Current vault passphrase"
                value={currentVaultPass}
                onChange={(e) => setCurrentVaultPass(e.target.value)}
              />
            </div>
          )}
          <div>
            <label
              htmlFor="toggle-master-pass"
              className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
            >
              Master passphrase
            </label>
            <PassphraseInput
              id="toggle-master-pass"
              autoCompleteMode="current-password"
              aria-label="Master passphrase"
              value={masterPass}
              onChange={(e) => setMasterPass(e.target.value)}
            />
          </div>
          {goingToUnsynced && (
            <div>
              <label
                htmlFor="toggle-new-vault-pass"
                className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
              >
                New vault passphrase
              </label>
              <PassphraseInput
                id="toggle-new-vault-pass"
                autoCompleteMode="new-password"
                aria-label="New vault passphrase"
                value={newVaultPass}
                onChange={(e) => setNewVaultPass(e.target.value)}
              />
              <PassphraseStrength passphrase={newVaultPass} />
              {!newStrong && newVaultPass.length > 0 && (
                <div className="mt-1.5 text-[12px] text-danger">
                  Too weak — use 5+ random words.
                </div>
              )}
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ————— Retry-pending dialog ————————————————————————————————

function RetryDialog({
  target,
  onCancel,
  onSubmit,
}: {
  readonly target: VaultEntry;
  readonly onCancel: () => void;
  readonly onSubmit: (oldMaster: string, currentMaster: string) => void;
}) {
  const [oldMaster, setOldMaster] = useState("");
  const [currentMaster, setCurrentMaster] = useState("");
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Retry ${target.display_name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-medium tracking-tight">
          Retry rotation for {target.display_name}
        </h2>
        <p className="mb-4 text-[13px] text-text-soft">
          Type the OLD master (used to unlock {target.display_name}) and the
          CURRENT master (the one set during rotation).
        </p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (oldMaster.length > 0 && currentMaster.length > 0) {
              onSubmit(oldMaster, currentMaster);
              setOldMaster("");
              setCurrentMaster("");
            }
          }}
        >
          <div>
            <label
              htmlFor="vaults-retry-old-master"
              className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
            >
              Old master
            </label>
            <PassphraseInput
              id="vaults-retry-old-master"
              autoCompleteMode="current-password"
              aria-label="Old master"
              value={oldMaster}
              onChange={(e) => setOldMaster(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="vaults-retry-current-master"
              className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
            >
              Current master
            </label>
            <PassphraseInput
              id="vaults-retry-current-master"
              autoCompleteMode="current-password"
              aria-label="Current master"
              value={currentMaster}
              onChange={(e) => setCurrentMaster(e.target.value)}
            />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={oldMaster.length === 0 || currentMaster.length === 0}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Start
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
